/**
 * VideoCall – Fitnova consultation via Ratomeet (WebRTC + Socket.io)
 * Signaling server: Ratomeet/server
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../api/axios';
import useSocket from '../ratomeet/useSocket';
import useMediaDevices from '../ratomeet/useMediaDevices';
import useWebRTC from '../ratomeet/useWebRTC';
import SendPrescriptionModal from '../components/SendPrescriptionModal';

function getApiError(err) {
  const data = err.response?.data;
  if (!data) {
    return err.message || 'Failed to join call';
  }
  if (typeof data === 'string') return data;
  return (
    data.error ||
    data.message ||
    data.msg ||
    data.detail ||
    (Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : null) ||
    'Failed to join call'
  );
}

function RemoteVideo({ stream }) {
  const videoRef = useRef(null);

  useEffect(() => {
    const el = videoRef.current;
    if (el && stream) {
      el.srcObject = stream;
      el.play().catch(() => { });
    }
  }, [stream]);

  return (
    <video
      ref={videoRef}
      autoPlay
      playsInline
      style={styles.remoteVideo}
    />
  );
}

/** Plays remote audio without showing a camera feed */
function RemoteAudio({ stream }) {
  const audioRef = useRef(null);

  useEffect(() => {
    const el = audioRef.current;
    if (el && stream) {
      el.srcObject = stream;
      el.play().catch(() => { });
    }
  }, [stream]);

  return <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />;
}

function AudioParticipantTile({ label, subtitle, isMuted, isWaiting }) {
  return (
    <div style={styles.audioTile}>
      <div style={styles.audioAvatar}>
        <i className={`bi ${isWaiting ? 'bi-hourglass-split' : 'bi-person-fill'}`} style={{ fontSize: 48, color: '#fff' }} />
      </div>
      <h5 className="text-white fw-bold mb-1 mt-3">{label}</h5>
      {subtitle && <p className="text-white-50 small mb-0">{subtitle}</p>}
      {!isWaiting && (
        <div className="mt-3 d-flex align-items-center gap-2">
          <span
            className="badge rounded-pill px-3 py-2"
            style={{
              background: isMuted ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)',
              color: isMuted ? '#fca5a5' : '#6ee7b7',
              border: `1px solid ${isMuted ? 'rgba(239,68,68,0.4)' : 'rgba(16,185,129,0.4)'}`,
            }}
          >
            <i className={`bi ${isMuted ? 'bi-mic-mute-fill' : 'bi-mic-fill'} me-1`} />
            {isMuted ? 'Muted' : 'On call'}
          </span>
        </div>
      )}
    </div>
  );
}

function VideoCall() {
  const { appointmentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isDoctor = location.pathname.includes('/doctor/');

  const socket = useSocket();
  const media = useMediaDevices();

  const [roomId, setRoomId] = useState(null);
  const [userName, setUserName] = useState('');
  const [isJoined, setIsJoined] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [waitingForDoctor, setWaitingForDoctor] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPrescription, setShowPrescription] = useState(false);
  const [bookingNotes, setBookingNotes] = useState('');
  const [bookingReport, setBookingReport] = useState(null);
  const [bookingReports, setBookingReports] = useState([]);
  const [showNotesWidget, setShowNotesWidget] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState({
    patient_name: 'Harsh Gupta',
    patient_age: '28',
    patient_gender: 'Male',
    slot_date: '24 July 2026',
    slot_start_time: '02:00 PM',
  });
  // Appointment info for prescription modal (built after call initialises)
  const [appointmentInfo, setAppointmentInfo] = useState(null);
  const [callType, setCallType] = useState('VIDEO');

  const localVideoRef = useRef(null);
  const chatEndRef = useRef(null);
  const showChatRef = useRef(false);
  const timerRef = useRef(null);
  const retryIntervalRef = useRef(null);
  const hasCleanedUp = useRef(false);
  const roomIdRef = useRef(null);

  const webrtc = useWebRTC(socket, roomId, media.localStream);

  const remoteEntries = Object.entries(webrtc.remoteStreams);
  const hasRemoteUser = remoteEntries.length > 0;
  const isAudioCall = callType === 'AUDIO';

  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDownloadReport = async (reportUrl) => {
    const url = reportUrl || bookingReport;
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const fileName = url.split('/').pop() || 'medical_report.pdf';
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Direct blob download failed, falling back to window.open:', err);
      window.open(url, '_blank');
    }
  };

  const leaveCall = useCallback(
    async (notifyBackend = true) => {
      if (hasCleanedUp.current) return;
      hasCleanedUp.current = true;

      try {
        if (roomIdRef.current) {
          socket.leaveRoom(roomIdRef.current);
        }
        webrtc.cleanup();
        media.stopMedia();
        if (notifyBackend) {
          await api.post(`video/end/${appointmentId}/`);
        }
      } catch (err) {
        console.error('Error during cleanup:', err);
      }
    },
    [socket, webrtc, media, appointmentId]
  );

  const handleSessionEnd = useCallback(async () => {
    setSessionEnded(true);
    if (timerRef.current) clearInterval(timerRef.current);
    await leaveCall(true);
    setTimeout(() => {
      if (!isDoctor) {
        navigate(`/feedback/${appointmentId}`);
      } else {
        navigate('/doctor/appointments');
      }
    }, 5000);
  }, [leaveCall, navigate, isDoctor, appointmentId]);

  const initCall = useCallback(async () => {
    try {
      let res;
      if (isDoctor) {
        res = await api.post(`video/create/${appointmentId}/`);
      } else {
        res = await api.get(`video/join/${appointmentId}/`);
      }

      const data = res.data;
      const channel = data.room_id || data.channel_name;
      if (!channel) {
        throw new Error('Server did not return a meeting room. Please try again.');
      }

      const displayName = data.user_name || (isDoctor ? 'Doctor' : 'Patient');

      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
      setWaitingForDoctor(false);
      setError('');

      setRoomId(channel);
      roomIdRef.current = channel;
      setUserName(displayName);

      let resolvedCallType = data.call_type === 'AUDIO' ? 'AUDIO' : 'VIDEO';
      // Fallback: if join/create omitted call_type, read from session-info
      if (data.call_type !== 'AUDIO' && data.call_type !== 'VIDEO') {
        try {
          const infoRes = await api.get(`video/session-info/${appointmentId}/`);
          if (infoRes.data?.call_type === 'AUDIO') {
            resolvedCallType = 'AUDIO';
          }
        } catch (_) {
          /* keep VIDEO default */
        }
      }
      setCallType(resolvedCallType);

      const effectiveTimeRemaining =
        data.time_remaining > 0 ? data.time_remaining : (data.slot_duration || 30) * 60;
      setTimeRemaining(effectiveTimeRemaining);

      setBookingNotes(data.patient_notes || '');
      const reports = Array.isArray(data.medical_reports) && data.medical_reports.length
        ? data.medical_reports
        : (data.medical_report ? [data.medical_report] : []);
      setBookingReports(reports);
      setBookingReport(reports[0] || null);
      if (data.patient_name || data.slot_date) {
        setAppointmentDetails({
          patient_name: data.patient_name || 'Harsh Gupta',
          patient_age: data.patient_age || '28',
          patient_gender: data.patient_gender || 'Male',
          slot_date: data.slot_date || '24 July 2026',
          slot_start_time: data.slot_start_time || '02:00 PM',
        });
      }

      // Build minimal appointment object for SendPrescriptionModal
      if (isDoctor) {
        setAppointmentInfo({
          appointment_id: Number(appointmentId),
          patient_name: data.patient_name || 'Patient',
          patient_email: data.patient_email || '',
          date: data.slot_date || '',
          start_time: data.slot_start_time || '',
        });
      }

      try {
        // AUDIO = mic only; VIDEO = camera + mic
        const enableVideo = resolvedCallType !== 'AUDIO';
        await media.getUserMedia(enableVideo, true);
      } catch (mediaErr) {
        setError(
          mediaErr.message?.includes('Permission')
            ? (resolvedCallType === 'AUDIO'
              ? 'Microphone permission denied. Allow access and try again.'
              : 'Camera/microphone permission denied. Allow access and try again.')
            : `Could not access ${resolvedCallType === 'AUDIO' ? 'microphone' : 'camera/microphone'}: ${mediaErr.message}`
        );
        setLoading(false);
        return;
      }

      setLoading(false);
    } catch (err) {
      console.error('Error joining call:', err);
      const status = err.response?.status;
      const errData = err.response?.data || {};
      const errorMessage = getApiError(err);
      const sessionActive = errData.session_active;
      const redirectToWaitingRoom = errData.redirect_to_waiting_room;
      const slotEnded = errData.slot_ended;

      if (status === 403 && (redirectToWaitingRoom || errData.message?.includes('waiting room'))) {
        navigate(
          isDoctor
            ? `/doctor/waiting-room/${appointmentId}`
            : `/user/waiting-room/${appointmentId}`
        );
        return;
      }

      if (status === 401) {
        setError(
          isDoctor
            ? 'Doctor session expired. Please login again.'
            : 'Session expired. Please login again.'
        );
        setLoading(false);
        return;
      }

      if (status === 400 && slotEnded) {
        setError('The scheduled slot time has ended. The meeting has been closed.');
        setLoading(false);
        setTimeout(() => {
          navigate(isDoctor ? '/doctor/appointments' : '/user/bookings');
        }, 3000);
        return;
      }

      // Patient: doctor has not started the call yet — keep waiting (same flow as before)
      if (
        !isDoctor &&
        (status === 404 ||
          sessionActive === false ||
          errorMessage.toLowerCase().includes('wait for the doctor') ||
          errorMessage.toLowerCase().includes('no active call'))
      ) {
        setWaitingForDoctor(true);
        setLoading(false);
        if (!retryIntervalRef.current) {
          retryIntervalRef.current = setInterval(() => initCall(), 3000);
        }
        return;
      }

      if (isDoctor && errorMessage.toLowerCase().includes('accepted')) {
        setError('Only accepted appointments can use video call.');
      } else {
        setError(errorMessage);
      }
      setLoading(false);
    }
  }, [appointmentId, isDoctor, navigate, media]);

  useEffect(() => {
    const initCsrf = async () => {
      try {
        await api.get(isDoctor ? 'doctor/login/' : 'user/login/');
      } catch (csrfErr) {
        console.warn('CSRF init failed (non-critical):', csrfErr);
      }
    };
    initCsrf();
  }, [isDoctor]);

  useEffect(() => {
    initCall();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (retryIntervalRef.current) clearInterval(retryIntervalRef.current);
      leaveCall(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only attach camera stream for VIDEO calls
    if (callType === 'AUDIO') return;
    if (localVideoRef.current && media.localStream) {
      localVideoRef.current.srcObject = media.localStream;
    }
  }, [media.localStream, callType]);

  const hasJoinedRoomRef = useRef(false);

  useEffect(() => {
    if (!roomId || !userName || !media.localStream || !socket.isConnected) {
      return;
    }
    if (!hasJoinedRoomRef.current) {
      console.log('[Ratomeet] Joining room:', roomId, 'as', userName);
      socket.joinRoom(roomId, userName);
      hasJoinedRoomRef.current = true;
    }
    setIsJoined(true);
  }, [roomId, userName, media.localStream, socket.isConnected, socket]);

  useEffect(() => {
    if (!socket.isConnected) {
      hasJoinedRoomRef.current = false;
    }
  }, [socket.isConnected]);

  // In-call chat (Ratomeet signaling: chat-message)
  useEffect(() => {
    showChatRef.current = showChat;
    if (showChat) setUnreadCount(0);
  }, [showChat]);

  useEffect(() => {
    if (!socket.isConnected) return;

    const handleChatMessage = (data) => {
      setChatMessages((prev) => [
        ...prev,
        {
          id: `${data.from}-${data.timestamp || Date.now()}`,
          sender: data.userName || (isDoctor ? 'Patient' : 'Doctor'),
          text: data.message,
          timestamp: data.timestamp || Date.now(),
          isOwn: false,
        },
      ]);
      if (!showChatRef.current) {
        setUnreadCount((c) => c + 1);
      }
    };

    socket.on('chat-message', handleChatMessage);
    return () => socket.off('chat-message', handleChatMessage);
  }, [socket, isDoctor]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSendChat = (e) => {
    e?.preventDefault?.();
    const text = chatInput.trim();
    if (!text || !roomId) return;

    socket.sendChatMessage(text, roomId);
    setChatMessages((prev) => [
      ...prev,
      {
        id: `own-${Date.now()}`,
        sender: userName || 'You',
        text,
        timestamp: Date.now(),
        isOwn: true,
      },
    ]);
    setChatInput('');
  };

  useEffect(() => {
    if (!isJoined || sessionEnded) return;

    timerRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          handleSessionEnd();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isJoined, sessionEnded, handleSessionEnd]);

  const handleEndCall = async () => {
    await leaveCall(true);
    if (isDoctor) {
      navigate('/doctor/appointments');
    } else {
      navigate(`/feedback/${appointmentId}`);
    }
  };

  if (sessionEnded) {
    return (
      <div style={styles.sessionEndedContainer}>
        <div style={styles.sessionEndedCard}>
          <div style={styles.sessionEndedIcon}>
            <i className="bi bi-check-circle-fill" style={{ fontSize: '64px', color: '#10b981' }}></i>
          </div>
          <h2 style={styles.sessionEndedTitle}>Your session has ended</h2>
          <p style={styles.sessionEndedMessage}>
            Thank you for using our video consultation service.
          </p>
          <p style={styles.redirectMessage}>Redirecting...</p>
          <div style={styles.spinner}></div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>
          {socket.connectionError
            ? `Connecting to signaling server... (${socket.connectionError})`
            : 'Connecting to call...'}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <i className="bi bi-exclamation-circle" style={{ fontSize: '48px', color: '#ef4444' }}></i>
        <h3 style={styles.errorTitle}>Unable to Join Call</h3>
        <p style={styles.errorMessage}>{error}</p>
        <button type="button" onClick={() => navigate(-1)} style={styles.backButton}>
          Go Back
        </button>
      </div>
    );
  }

  if (waitingForDoctor) {
    return (
      <div style={styles.waitingForDoctorContainer}>
        <div style={styles.waitingForDoctorCard}>
          <div style={styles.pulsingIcon}>
            <i className="bi bi-person-video3" style={{ fontSize: '64px', color: '#fff' }}></i>
          </div>
          <h2 style={styles.waitingTitle}>Waiting for Doctor</h2>
          <p style={styles.waitingMessage}>
            The doctor hasn&apos;t started the call yet. Please wait...
          </p>
          <div style={styles.waitingSpinner}></div>
          <button
            type="button"
            onClick={() => {
              if (retryIntervalRef.current) clearInterval(retryIntervalRef.current);
              navigate('/user/bookings');
            }}
            style={styles.cancelWaitButton}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, backgroundColor: '#070D1D' }}>
      {/* Top Header Bar */}
      <div className="d-flex align-items-center justify-content-between px-4 py-2 text-white border-bottom border-secondary border-opacity-25" style={{ background: '#0B132B', zIndex: 50, height: '60px' }}>
        {/* Left: Brand Logo */}
        <div className="d-flex align-items-center">
          <div className="bg-primary text-white rounded-3 p-1 px-2 me-2 d-flex align-items-center justify-content-center" style={{ width: '34px', height: '34px', background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' }}>
            <i className="bi bi-plus-lg fs-5 fw-bold text-white"></i>
          </div>
          <div>
            <h6 className="fw-bold text-white mb-0 tracking-wide" style={{ fontSize: '15px', letterSpacing: '1px' }}>FITNOVA</h6>
            <small className="text-white-50 d-block uppercase" style={{ fontSize: '9px', letterSpacing: '1.5px', marginTop: '-3px' }}>HEALTHCARE</small>
          </div>
        </div>

        {/* Center: Time Remaining + Call type */}
        <div className="d-flex align-items-center gap-2">
          <span
            className="badge rounded-pill px-3 py-2"
            style={{
              background: isAudioCall ? 'rgba(6,182,212,0.2)' : 'rgba(37,99,235,0.2)',
              color: isAudioCall ? '#67e8f9' : '#93c5fd',
              border: `1px solid ${isAudioCall ? 'rgba(6,182,212,0.4)' : 'rgba(37,99,235,0.4)'}`,
              fontSize: '12px',
            }}
          >
            <i className={`bi ${isAudioCall ? 'bi-telephone-fill' : 'bi-camera-video-fill'} me-1`} />
            {isAudioCall ? 'Audio Call' : 'Video Call'}
          </span>
          <div className="d-flex align-items-center px-3 py-1 rounded-pill bg-dark bg-opacity-70 border border-secondary border-opacity-30" style={{ background: 'rgba(15, 23, 42, 0.8)' }}>
            <i className="bi bi-clock me-2 text-primary fs-6"></i>
            <span className="small text-white-50 me-2">Time Remaining</span>
            <span className="fw-bold text-white fs-6">{formatTime(timeRemaining)}</span>
          </div>
        </div>

        {/* Right: Encrypted & Dots */}
        <div className="d-flex align-items-center gap-3">
          <div className="d-flex align-items-center bg-success bg-opacity-10 text-success px-3 py-1 rounded-pill border border-success border-opacity-25 small fw-medium" style={{ fontSize: '13px' }}>
            <i className="bi bi-shield-lock-fill me-1"></i>
            Secure & Encrypted
          </div>
          {/* <button type="button" className="btn btn-sm btn-link text-white-50 p-1">
            <i className="bi bi-three-dots-vertical fs-5"></i>
          </button> */}
        </div>
      </div>

      <div className="d-flex flex-grow-1 overflow-hidden position-relative">
        {/* Main streams — video tiles OR audio avatars */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            gap: '20px',
            padding: '24px',
            backgroundColor: '#070D1D',
            position: 'relative',
            minHeight: 0,
            transition: 'all 0.3s ease',
          }}
        >
          {isAudioCall ? (
            <>
              <div style={hasRemoteUser ? styles.videoStreamContainer : styles.fullLocalContainer}>
                <AudioParticipantTile
                  label="You"
                  subtitle={userName || (isDoctor ? 'Doctor' : 'Patient')}
                  isMuted={!media.isAudioEnabled}
                />
              </div>

              {hasRemoteUser ? (
                remoteEntries.map(([peerId, stream]) => (
                  <div key={peerId} style={styles.videoStreamContainer}>
                    <RemoteAudio stream={stream} />
                    <AudioParticipantTile
                      label={isDoctor ? 'Patient' : 'Doctor'}
                      subtitle="Connected"
                      isMuted={false}
                    />
                  </div>
                ))
              ) : (
                <div style={styles.videoStreamContainer}>
                  <AudioParticipantTile
                    label={isDoctor ? 'Patient' : 'Doctor'}
                    subtitle={
                      !socket.isConnected
                        ? 'Connecting…'
                        : `Waiting for ${isDoctor ? 'patient' : 'doctor'} to join…`
                    }
                    isWaiting
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div style={hasRemoteUser ? styles.videoStreamContainer : styles.fullLocalContainer}>
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  style={styles.localVideo}
                />
                {!media.isVideoEnabled && (
                  <div style={styles.videoOffOverlay}>
                    <i className="bi bi-camera-video-off" style={{ fontSize: '48px' }}></i>
                    <p>Camera Off</p>
                  </div>
                )}
                <div className="badge bg-primary px-3 py-1 rounded-pill shadow-sm" style={{ position: 'absolute', top: 16, left: 16, background: '#1D4ED8', fontSize: '13px', zIndex: 10 }}>You</div>
                <div style={{ position: 'absolute', bottom: 16, left: 16, background: 'rgba(15,23,42,0.8)', padding: '4px 10px', borderRadius: '8px', zIndex: 10 }}>
                  <i className="bi bi-bar-chart-fill text-success fs-6"></i>
                </div>
              </div>

              {hasRemoteUser ? (
                remoteEntries.map(([peerId, stream]) => (
                  <div key={peerId} style={styles.videoStreamContainer}>
                    <RemoteVideo stream={stream} />
                    <div className="badge bg-primary px-3 py-1 rounded-pill shadow-sm" style={{ position: 'absolute', top: 16, left: 16, background: '#1D4ED8', fontSize: '13px', zIndex: 10 }}>{isDoctor ? 'Patient' : 'Doctor'}</div>
                    <div style={{ position: 'absolute', bottom: 16, left: 16, background: 'rgba(15,23,42,0.8)', padding: '4px 10px', borderRadius: '8px', zIndex: 10 }}>
                      <i className="bi bi-bar-chart-fill text-success fs-6"></i>
                    </div>
                  </div>
                ))
              ) : (
                <div style={styles.videoStreamContainer}>
                  <div style={styles.waitingOverlay}>
                    <div style={styles.spinnerSmall}></div>
                    <p className="fw-semibold mt-3 text-white">
                      {!socket.isConnected
                        ? 'Connecting to video server… Start Ratomeet server on port 3100'
                        : `Waiting for ${isDoctor ? 'patient' : 'doctor'} to join…`}
                    </p>
                    {roomId && (
                      <p style={{ fontSize: 12, opacity: 0.7 }} className="text-white-50">Room: {roomId}</p>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Prescription Sidebar — Doctor Only (same layout as Booking Info) */}
        {isDoctor && showPrescription && appointmentInfo && (
          <SendPrescriptionModal
            appointment={appointmentInfo}
            asSidebar
            onClose={() => setShowPrescription(false)}
            onSaved={() => setShowPrescription(false)}
          />
        )}

        {/* Booking Info & Report Sidebar Panel */}
        {showNotesWidget && (
          <div
            style={{
              width: '380px',
              height: '100%',
              backgroundColor: '#0A1227',
              borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              overflowY: 'auto',
              zIndex: 90,
              position: 'relative',
            }}
          >
            {/* Panel Header */}
            <div className="d-flex justify-content-between align-items-center border-bottom border-secondary border-opacity-25 pb-3">
              <div className="d-flex align-items-center">
                <i className="bi bi-file-earmark-medical-fill text-primary fs-4 me-2"></i>
                <h5 className="fw-bold mb-0 text-white" style={{ fontSize: '17px' }}>Booking Info & Report</h5>
              </div>
              <button
                type="button"
                className="btn btn-sm btn-link text-white-50 p-0 text-decoration-none"
                onClick={() => setShowNotesWidget(false)}
              >
                <i className="bi bi-x-lg fs-5"></i>
              </button>
            </div>

            {/* Card 1: Patient Details */}
            <div className="p-3 rounded-4 border border-secondary border-opacity-25 d-flex align-items-start gap-3" style={{ background: '#121D38' }}>
              <div className="bg-primary bg-opacity-10 text-primary rounded-3 p-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '42px', height: '42px' }}>
                <i className="bi bi-person-fill fs-4 text-white"></i>
              </div>
              <div>
                <small className="text-white-50 d-block small" style={{ fontSize: '11px' }}>Patient</small>
                <h6 className="fw-bold text-white mb-1">{appointmentDetails.patient_name}</h6>
                <small className="text-white-50 d-block" style={{ fontSize: '12px' }}>
                  Age: {appointmentDetails.patient_age} &nbsp;•&nbsp; {appointmentDetails.patient_gender}
                </small>
              </div>
            </div>

            {/* Card 2: Appointment Details */}
            <div className="p-3 rounded-4 border border-secondary border-opacity-25 d-flex align-items-start gap-3" style={{ background: '#121D38' }}>
              <div className="bg-primary bg-opacity-10 text-primary rounded-3 p-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '42px', height: '42px' }}>
                <i className="bi bi-calendar-event-fill fs-5 text-white"></i>
              </div>
              <div>
                <small className="text-white-50 d-block small" style={{ fontSize: '11px' }}>Appointment</small>
                <h6 className="fw-bold text-white mb-1">{appointmentDetails.slot_date} &nbsp;•&nbsp; {appointmentDetails.slot_start_time}</h6>
                <small className="text-white-50 d-block" style={{ fontSize: '12px' }}>
                  Consultation Type: Video Call
                </small>
              </div>
            </div>

            {/* Card 3: Symptoms / Notes */}
            <div className="p-3 rounded-4 border border-secondary border-opacity-25 d-flex align-items-start gap-3" style={{ background: '#121D38' }}>
              <div className="bg-primary bg-opacity-10 text-primary rounded-3 p-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '42px', height: '42px' }}>
                <i className="bi bi-chat-left-text-fill fs-5 text-white"></i>
              </div>
              <div className="flex-grow-1">
                <small className="text-white-50 d-block small" style={{ fontSize: '11px' }}>Symptoms / Notes</small>
                <p className="fw-semibold text-white mb-1 small">{bookingNotes || 'Headache, Fever, Body pain'}</p>
                <span className="text-primary small cursor-pointer fw-medium" style={{ fontSize: '12px' }}>View more</span>
              </div>
            </div>

            {/* Card 4: Doctor Report Section */}
            <div className="mt-1">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold text-white mb-0" style={{ fontSize: '14px' }}>Doctor Report</h6>
                {bookingReports.length > 0 && (
                  <span className="badge bg-success bg-opacity-20 text-white border border-success border-opacity-25 px-2 py-1 small rounded-2" style={{ fontSize: '10px' }}>
                    {bookingReports.length > 1 ? `${bookingReports.length} Uploaded` : 'Uploaded'}
                  </span>
                )}
              </div>

              {/* Report File Cards */}
              {bookingReports.length > 0 ? (
                bookingReports.map((reportUrl, idx) => (
                  <div key={`${reportUrl}-${idx}`} className="mb-3">
                    <div className="p-3 rounded-4 mb-2 border border-secondary border-opacity-25 d-flex align-items-center gap-3" style={{ background: '#121D38' }}>
                      <div className="bg-danger bg-opacity-20 text-danger rounded-3 p-2 d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '42px', height: '42px' }}>
                        <i className="bi bi-file-earmark-pdf-fill fs-4 text-white"></i>
                      </div>
                      <div className="overflow-hidden">
                        <h6 className="fw-bold text-white mb-0 text-truncate" style={{ fontSize: '14px' }}>
                          {reportUrl.split('/').pop() || `report-${idx + 1}.pdf`}
                        </h6>
                        <small className="text-white-50 d-block text-truncate" style={{ fontSize: '11px' }}>
                          Uploaded for consultation
                        </small>
                      </div>
                    </div>
                    <div className="d-flex flex-column gap-2">
                      <a
                        href={reportUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary w-100 py-2 rounded-3 fw-semibold text-decoration-none d-flex align-items-center justify-content-center border-primary border-opacity-50"
                        style={{ color: '#60a5fa', borderColor: '#2563eb', fontSize: '13px' }}
                      >
                        <i className="bi bi-eye me-2"></i> View Report{bookingReports.length > 1 ? ` ${idx + 1}` : ''}
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDownloadReport(reportUrl)}
                        className="btn btn-outline-primary w-100 py-2 rounded-3 fw-semibold text-decoration-none d-flex align-items-center justify-content-center border-primary border-opacity-50"
                        style={{ color: '#60a5fa', borderColor: '#2563eb', fontSize: '13px' }}
                      >
                        <i className="bi bi-download me-2"></i> Download
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-3 rounded-4 mb-3 border border-secondary border-opacity-25 text-center text-white-50 small" style={{ background: '#121D38' }}>
                  No doctor report attached to this appointment.
                </div>
              )}

              {/* Blue Info Notice Box */}
              <div className="p-3 rounded-4 bg-primary bg-opacity-10 border border-primary border-opacity-25 d-flex align-items-start gap-2">
                <i className="bi bi-info-circle-fill text-primary fs-5 flex-shrink-0 mt-1"></i>
                <span className="small text-white" style={{ fontSize: '11px', lineHeight: '1.4' }}>
                  Report is shared by the doctor. Please contact your doctor for any queries.
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Floating Control Bar */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          left: (showNotesWidget || showPrescription) ? 'calc(50% - 190px)' : (showChat ? 'calc(50% - 170px)' : '50%'),
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
          backgroundColor: '#0F172A',
          padding: '10px 28px',
          borderRadius: '50px',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.6)',
          zIndex: 100,
          transition: 'all 0.3s ease',
        }}
      >
        {/* Mute */}
        <div className="d-flex flex-column align-items-center">
          <button
            type="button"
            onClick={media.toggleAudio}
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: !media.isAudioEnabled ? '#ef4444' : '#334155',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
            }}
            title={media.isAudioEnabled ? 'Mute' : 'Unmute'}
          >
            <i className={`bi ${!media.isAudioEnabled ? 'bi-mic-mute-fill' : 'bi-mic-fill'}`}></i>
          </button>
          <span className="small text-white-50 mt-1" style={{ fontSize: '11px' }}>
            {media.isAudioEnabled ? 'Mute' : 'Unmute'}
          </span>
        </div>

        {/* Camera — video calls only */}
        {callType !== 'AUDIO' && (
        <div className="d-flex flex-column align-items-center">
          <button
            type="button"
            onClick={media.toggleVideo}
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: !media.isVideoEnabled ? '#ef4444' : '#334155',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
            }}
            title={media.isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
          >
            <i className={`bi ${!media.isVideoEnabled ? 'bi-camera-video-off-fill' : 'bi-camera-video-fill'}`}></i>
          </button>
          <span className="small text-white-50 mt-1" style={{ fontSize: '11px' }}>Camera</span>
        </div>
        )}

        {/* Chat */}
        <div className="d-flex flex-column align-items-center">
          <button
            type="button"
            onClick={() => setShowChat((v) => !v)}
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: showChat ? '#2563eb' : '#334155',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
              position: 'relative',
            }}
            title={showChat ? 'Close chat' : 'Open chat'}
          >
            <i className="bi bi-chat-dots-fill"></i>
            {unreadCount > 0 && !showChat && (
              <span style={styles.chatBadge}>{unreadCount > 9 ? '9+' : unreadCount}</span>
            )}
          </button>
          <span className="small text-white-50 mt-1" style={{ fontSize: '11px' }}>Chat</span>
        </div>

        {/* Notes */}
        <div className="d-flex flex-column align-items-center">
          <button
            type="button"
            onClick={() => {
              setShowNotesWidget((v) => !v);
              setShowPrescription(false);
            }}
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: showNotesWidget ? '#2563eb' : '#334155',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
            }}
            title="View Booking Info & Report"
          >
            <i className="bi bi-file-earmark-medical-fill"></i>
          </button>
          <span className="small text-white-50 mt-1" style={{ fontSize: '11px' }}>Notes</span>
        </div>

        {/* Prescription — Doctor Only */}
        {isDoctor && (
          <div className="d-flex flex-column align-items-center">
            <button
              type="button"
              onClick={() => {
                setShowPrescription((v) => !v);
                setShowNotesWidget(false);
              }}
              style={{
                width: '46px',
                height: '46px',
                borderRadius: '50%',
                border: 'none',
                backgroundColor: showPrescription ? '#059669' : '#334155',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '18px',
              }}
              title="Write prescription"
            >
              <i className="bi bi-file-medical-fill"></i>
            </button>
            <span className="small text-white-50 mt-1" style={{ fontSize: '11px' }}>Rx</span>
          </div>
        )}

        {/* End Call */}
        <div className="d-flex flex-column align-items-center">
          <button
            type="button"
            onClick={handleEndCall}
            style={{
              width: '46px',
              height: '46px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#dc2626',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: '18px',
            }}
            title="End call"
          >
            <i className="bi bi-telephone-x-fill"></i>
          </button>
          <span className="small text-white-50 mt-1" style={{ fontSize: '11px' }}>End Call</span>
        </div>
      </div>

      {showChat && (
        <div style={styles.chatPanel}>
          <div style={styles.chatHeader}>
            <h3 style={styles.chatTitle}>In-call chat</h3>
            <button type="button" onClick={() => setShowChat(false)} style={styles.chatCloseBtn}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div style={styles.chatMessages}>
            {chatMessages.length === 0 ? (
              <div style={styles.chatEmpty}>
                <p style={{ margin: 0, fontSize: 14 }}>No messages yet</p>
                <span style={{ fontSize: 12, opacity: 0.7 }}>
                  Only people in this call can see messages
                </span>
              </div>
            ) : (
              chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  style={{
                    ...styles.chatMessage,
                    alignItems: msg.isOwn ? 'flex-end' : 'flex-start',
                  }}
                >
                  <div style={styles.messageHeader}>
                    <span style={{ ...styles.messageSender, color: msg.isOwn ? '#60a5fa' : '#9ca3af' }}>
                      {msg.isOwn ? 'You' : msg.sender}
                    </span>
                    <span style={styles.messageTime}>
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div
                    style={{
                      ...styles.messageBubble,
                      backgroundColor: msg.isOwn ? '#2563eb' : '#374151',
                    }}
                  >
                    {msg.text}
                  </div>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>
          <form style={styles.chatInputRow} onSubmit={handleSendChat}>
            <input
              type="text"
              placeholder="Send a message..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              maxLength={500}
              style={styles.chatInput}
            />
            <button
              type="submit"
              disabled={!chatInput.trim()}
              style={{
                ...styles.chatSendBtn,
                opacity: chatInput.trim() ? 1 : 0.5,
                cursor: chatInput.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              <i className="bi bi-send-fill"></i>
            </button>
          </form>
        </div>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.8; transform: scale(1.05); } }
      `}</style>

    </div>
  );
}

const styles = {
  container: {
    height: '100vh',
    backgroundColor: '#111827',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  timeDisplay: {
    position: 'absolute',
    top: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '10px 24px',
    borderRadius: '50px',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    backdropFilter: 'blur(10px)',
  },
  videoGridOne: {
    flex: 1,
    display: 'flex',
    padding: '72px 20px 120px',
    backgroundColor: '#000',
    position: 'relative',
    minHeight: 0,
    transition: 'margin-right 0.2s ease',
  },
  videoGridTwo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    gap: '16px',
    padding: '72px 20px 120px',
    backgroundColor: '#000',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'stretch',
    minHeight: 0,
    overflow: 'hidden',
    transition: 'margin-right 0.2s ease',
  },
  videoGridWithChat: {
    marginRight: '340px',
  },
  fullLocalContainer: {
    width: '100%',
    height: '100%',
    borderRadius: '16px',
    overflow: 'hidden',
    position: 'relative',
    border: '2px solid #374151',
    minWidth: 0,
  },
  videoStreamContainer: {
    flex: '1 1 0',
    height: '100%',
    minWidth: 0,
    borderRadius: '16px',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: '#1f2937',
    border: '2px solid #374151',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  localVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  remoteVideo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  videoLabel: {
    position: 'absolute',
    top: '14px',
    left: '14px',
    bottom: 'auto',
    padding: '6px 14px',
    backgroundColor: 'rgba(0,0,0,0.65)',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '600',
    backdropFilter: 'blur(4px)',
    zIndex: 10,
    pointerEvents: 'none',
  },
  videoOffOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#1f2937',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#9ca3af',
    gap: '12px',
  },
  audioTile: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 100%)',
    borderRadius: 16,
    border: '1px solid rgba(255,255,255,0.08)',
    padding: 24,
  },
  audioAvatar: {
    width: 110,
    height: 110,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 0 0 8px rgba(37,99,235,0.2)',
  },
  waitingOverlay: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
    color: '#9ca3af',
    textAlign: 'center',
  },
  spinnerSmall: {
    width: '40px',
    height: '40px',
    border: '3px solid #374151',
    borderTopColor: '#10b981',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  controlsBar: {
    position: 'absolute',
    bottom: '28px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '16px',
    padding: '14px 22px',
    backgroundColor: 'rgba(0,0,0,0.75)',
    borderRadius: '50px',
    backdropFilter: 'blur(10px)',
    zIndex: 150,
  },
  controlButton: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    border: 'none',
    color: '#fff',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  prescriptionButton: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    border: '2px solid #22c55e',
    backgroundColor: '#15803d',
    color: '#fff',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    transition: 'background-color 0.2s',
  },
  chatBadge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    minWidth: '18px',
    height: '18px',
    padding: '0 4px',
    borderRadius: '9px',
    backgroundColor: '#ef4444',
    color: '#fff',
    fontSize: '10px',
    fontWeight: '700',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  chatPanel: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: '340px',
    maxWidth: '90vw',
    backgroundColor: '#1f2937',
    borderLeft: '1px solid #374151',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 200,
    boxShadow: '-8px 0 24px rgba(0,0,0,0.4)',
  },
  chatHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #374151',
  },
  chatTitle: {
    margin: 0,
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
  },
  chatCloseBtn: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    cursor: 'pointer',
    fontSize: '16px',
    padding: '4px',
  },
  chatMessages: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px',
  },
  chatEmpty: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: '#9ca3af',
    gap: '4px',
  },
  chatMessage: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '14px',
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
  },
  messageSender: {
    fontSize: '12px',
    fontWeight: '600',
  },
  messageTime: {
    fontSize: '11px',
    color: '#6b7280',
  },
  messageBubble: {
    maxWidth: '90%',
    padding: '8px 12px',
    borderRadius: '12px',
    color: '#fff',
    fontSize: '14px',
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  chatInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    borderTop: '1px solid #374151',
  },
  chatInput: {
    flex: 1,
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: '24px',
    padding: '10px 16px',
    color: '#fff',
    fontSize: '14px',
    outline: 'none',
  },
  chatSendBtn: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#2563eb',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  endCallButton: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#ef4444',
    color: '#fff',
    fontSize: '20px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#111827',
    color: '#fff',
  },
  loadingText: { marginTop: '16px', fontSize: '16px' },
  spinner: {
    width: '50px',
    height: '50px',
    border: '3px solid #374151',
    borderTopColor: '#10b981',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  errorContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#111827',
    color: '#fff',
    gap: '16px',
    padding: '20px',
  },
  errorTitle: { fontSize: '24px', fontWeight: '700', margin: 0 },
  errorMessage: { fontSize: '16px', color: '#9ca3af', textAlign: 'center' },
  backButton: {
    padding: '12px 24px',
    backgroundColor: '#10b981',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    cursor: 'pointer',
    marginTop: '16px',
    fontSize: '14px',
    fontWeight: '600',
  },
  sessionEndedContainer: {
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#111827',
  },
  sessionEndedCard: {
    backgroundColor: '#1f2937',
    borderRadius: '24px',
    padding: '48px',
    textAlign: 'center',
    maxWidth: '400px',
  },
  sessionEndedIcon: { marginBottom: '24px' },
  sessionEndedTitle: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#fff',
    marginBottom: '16px',
  },
  sessionEndedMessage: { fontSize: '16px', color: '#9ca3af', marginBottom: '24px' },
  redirectMessage: { fontSize: '14px', color: '#6b7280', marginBottom: '16px' },
  waitingForDoctorContainer: {
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#111827',
  },
  waitingForDoctorCard: {
    backgroundColor: '#1f2937',
    borderRadius: '24px',
    padding: '48px',
    textAlign: 'center',
    maxWidth: '450px',
  },
  pulsingIcon: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    backgroundColor: '#0f52ba',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto 24px',
    animation: 'pulse 2s infinite',
  },
  waitingTitle: { fontSize: '28px', fontWeight: '700', color: '#fff', marginBottom: '16px' },
  waitingMessage: { fontSize: '16px', color: '#e5e7eb', marginBottom: '8px' },
  waitingSpinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #374151',
    borderTopColor: '#0f52ba',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 24px',
  },
  cancelWaitButton: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    border: '2px solid #4b5563',
    borderRadius: '8px',
    color: '#9ca3af',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    margin: '0 auto',
  },
};

export default VideoCall;
